import { DataService } from '../services/data.js';
import { formatMoney, formatHours, getIcon } from '../components/ui.js';
import { CONFIG } from '../config.js';
import Chart from 'chart.js/auto';

// Estado de filtros del Dashboard
const now = new Date();
let selectedMonth = now.getMonth();
let selectedYear = now.getFullYear();

// Instancias de Chart.js
let chartIngresos = null;
let chartValorHora = null;
let chartHoras = null;
let dashboardRenderRequestId = 0;

export function destroyCharts() {
  const chartCanvasIds = ['chart-evolucion-ingresos', 'chart-evolucion-valor-hora', 'chart-evolucion-horas'];
  
  chartCanvasIds.forEach(id => {
    try {
      const existing = Chart.getChart(id);
      if (existing) {
        existing.destroy();
      }
    } catch (e) {
      // Ignorar errores menores al destruir por id
    }
  });

  if (chartIngresos) {
    try { chartIngresos.destroy(); } catch (e) {}
    chartIngresos = null;
  }
  if (chartValorHora) {
    try { chartValorHora.destroy(); } catch (e) {}
    chartValorHora = null;
  }
  if (chartHoras) {
    try { chartHoras.destroy(); } catch (e) {}
    chartHoras = null;
  }
}


export async function renderDashboardView() {
  const metrics = await DataService.getDashboardMetrics({ month: selectedMonth, year: selectedYear });

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const periodFormatted = `${monthNames[selectedMonth]} ${selectedYear}`;
  const isCurrentMonth = selectedMonth === now.getMonth() && selectedYear === now.getFullYear();
  const goalPercent = Math.min(Math.round((metrics.currentMonthHours / CONFIG.MONTHLY_HOURS_GOAL) * 100), 100);

  return `
    <div class="space-y-8">
      <!-- Header / Greeting & Month-Year Filters -->
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-2 border-b border-border">
        <div>
          <h1 class="text-2xl font-bold tracking-tight text-text-primary">Hola, ${CONFIG.OWNER}</h1>
          <p class="text-xs text-text-secondary mt-0.5">
            Período seleccionado: <b class="text-text-primary font-medium">${periodFormatted}</b> ${isCurrentMonth ? '• Mes en curso' : '• Histórico'}
          </p>
        </div>

        <!-- Filter Controls -->
        <div class="flex flex-wrap items-center gap-2.5">
          <div class="flex items-center gap-1.5 bg-surface p-1 rounded-md border border-border shadow-sm">
            <span class="text-xs font-semibold text-text-secondary pl-2 pr-1">${getIcon('calendar', 'w-3.5 h-3.5 inline mr-1 text-text-secondary')}Período:</span>
            <select id="dashboard-filter-month" class="text-xs font-medium bg-surface-subtle border-0 rounded px-2.5 py-1 text-text-primary focus:ring-1 focus:ring-primary outline-none cursor-pointer">
              ${monthNames.map((m, idx) => `
                <option value="${idx}" ${idx === selectedMonth ? 'selected' : ''}>${m}</option>
              `).join('')}
            </select>

            <select id="dashboard-filter-year" class="text-xs font-medium bg-surface-subtle border-0 rounded px-2.5 py-1 text-text-primary focus:ring-1 focus:ring-primary outline-none cursor-pointer">
              ${[2024, 2025, 2026, 2027].map(y => `
                <option value="${y}" ${y === selectedYear ? 'selected' : ''}>${y}</option>
              `).join('')}
            </select>

            ${!isCurrentMonth ? `
              <button id="btn-reset-current-month" class="px-2 py-1 text-[11px] font-semibold text-primary hover:bg-surface-subtle rounded transition-colors" title="Volver al mes actual">
                Mes actual
              </button>
            ` : ''}
          </div>

          <span class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            Operación Equipo Alerce Activa
          </span>
        </div>
      </div>

      <!-- 4 KPI Metric Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <!-- 1. Ingresos Mensuales -->
        <div class="bg-surface p-5 rounded-md border border-border shadow-card flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-text-secondary uppercase tracking-wider">Ingresos Mensuales</span>
              <div class="p-2 rounded bg-surface-subtle text-text-primary">
                ${getIcon('dollar', 'w-4 h-4')}
              </div>
            </div>
            <div class="mt-3">
              <p class="text-2xl font-bold font-mono text-text-primary">${formatMoney(metrics.totalDevengado)}</p>
              <p class="text-xs text-text-secondary mt-1">Total devengado en ${monthNames[selectedMonth]}</p>
            </div>
          </div>

          <div class="mt-4 pt-2.5 border-t border-border/60 space-y-1.5 text-[10px] font-mono">
            <div class="flex items-center justify-between text-text-secondary">
              <span>Presupuesto: <b class="text-text-primary">${formatMoney(metrics.accruedByBudget)}</b></span>
              <span>Horas: <b class="text-text-primary">${formatMoney(metrics.accruedByHours)}</b></span>
            </div>
            <div class="flex items-center justify-between text-text-secondary">
              <span class="text-emerald-700">Recurrente: <b>${formatMoney(metrics.accruedRecurringTotal)}</b></span>
              <span>Puntual: <b class="text-text-primary">${formatMoney(metrics.accruedPunctualTotal)}</b></span>
            </div>
          </div>
        </div>

        <!-- 2. Valor de Hora (KPI Principal) -->
        <div class="bg-surface p-5 rounded-md border-2 border-primary/20 shadow-card flex flex-col justify-between relative overflow-hidden">
          <div class="absolute -right-6 -top-6 w-16 h-16 bg-primary/5 rounded-full pointer-events-none"></div>
          <div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-1.5">
                <span class="text-xs font-bold text-text-primary uppercase tracking-wider">Valor de tu Hora</span>
                <span class="text-[9px] font-mono px-1.5 py-0.5 rounded bg-primary text-white font-bold uppercase">KPI Principal</span>
              </div>
              <div class="p-2 rounded bg-emerald-50 text-emerald-700">
                ${getIcon('trendingUp', 'w-4 h-4')}
              </div>
            </div>
            <div class="mt-3">
              <div class="flex items-baseline gap-2">
                <p class="text-2xl font-bold font-mono text-text-primary">${formatMoney(metrics.hourlyValue)}<span class="text-xs font-normal text-text-secondary">/h</span></p>
              </div>
              <div class="mt-1.5 flex items-center gap-2">
                <span class="inline-flex items-center text-[11px] font-mono font-bold px-1.5 py-0.5 rounded ${
                  metrics.hourlyValueChangeReal >= 0 
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                    : 'bg-rose-50 text-rose-700 border border-rose-200'
                }">
                  ${metrics.hourlyValueChangeReal >= 0 ? '+' : ''}${metrics.hourlyValueChangeReal}% real
                </span>
                <span class="text-[10px] text-text-secondary font-mono">vs mes ant. (ajust. inflac.)</span>
              </div>
            </div>
          </div>

          <div class="mt-4 pt-2.5 border-t border-border/60 text-[10px] font-mono text-text-secondary flex items-center justify-between">
            <span>Nominal: <b class="text-text-primary">${metrics.hourlyValueChangeNominal >= 0 ? '+' : ''}${metrics.hourlyValueChangeNominal}%</b></span>
            <span title="Devengado acumulado dividido total de horas trabajadas">Ingresos / Horas totales</span>
          </div>
        </div>

        <!-- 3. Horas Trabajadas -->
        <div class="bg-surface p-5 rounded-md border border-border shadow-card flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-text-secondary uppercase tracking-wider">Horas Trabajadas</span>
              <div class="p-2 rounded bg-surface-subtle text-text-primary">
                ${getIcon('clock', 'w-4 h-4')}
              </div>
            </div>
            <div class="mt-3">
              <div class="flex items-baseline justify-between">
                <p class="text-2xl font-bold font-mono text-text-primary">${formatHours(metrics.currentMonthHours)}</p>
                <span class="text-xs font-mono text-text-secondary">${goalPercent}%</span>
              </div>
              <!-- Progress Bar -->
              <div class="w-full bg-surface-subtle h-2 rounded-full mt-2 overflow-hidden border border-border">
                <div class="bg-primary h-full rounded-full transition-all duration-500" style="width: ${goalPercent}%"></div>
              </div>
              <div class="flex items-center justify-between text-[11px] text-text-secondary mt-1 font-mono">
                <span>Meta: ${CONFIG.MONTHLY_HOURS_GOAL}h</span>
                <span class="text-text-primary font-medium">${formatHours(metrics.currentWeekHours)} esta semana</span>
              </div>
            </div>
          </div>

          <div class="mt-4 pt-2.5 border-t border-border/60 flex items-center justify-between text-[10px] font-mono text-text-secondary">
            <span>Fijo: <b class="text-text-primary">${formatHours(metrics.currentMonthFixedHours)}</b> • Reales: <b class="text-text-primary">${formatHours(metrics.currentMonthTmHours)}</b></span>
            <button id="btn-open-hours-breakdown" class="text-primary hover:underline font-semibold" title="Ver detalle de horas por proyecto">
              Ver detalle
            </button>
          </div>
        </div>

        <!-- 4. Punto de Equilibrio -->
        <div class="bg-surface p-5 rounded-md border border-border shadow-card flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-text-secondary uppercase tracking-wider">Punto de Equilibrio</span>
              <div class="p-2 rounded ${metrics.isCovered ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}">
                ${getIcon('target', 'w-4 h-4')}
              </div>
            </div>
            <div class="mt-3">
              ${metrics.isCovered ? `
                <p class="text-2xl font-bold font-mono text-emerald-600">
                  Cubierto ${metrics.breakEvenDate ? `el ${metrics.breakEvenDate}` : ''}
                </p>
                <p class="text-xs text-emerald-700 font-medium mt-1">${metrics.coveredPercent}% de costos cubiertos</p>
              ` : `
                <p class="text-2xl font-bold font-mono text-amber-700">
                  ${metrics.coveredPercent}% cubierto
                </p>
                <p class="text-xs text-text-secondary mt-1">Faltan ${formatMoney(Math.max(0, metrics.fixedCost - metrics.totalDevengado))}</p>
              `}
              <!-- Progress Bar -->
              <div class="w-full bg-surface-subtle h-2 rounded-full mt-2 overflow-hidden border border-border">
                <div class="${metrics.isCovered ? 'bg-emerald-500' : 'bg-amber-500'} h-full rounded-full transition-all duration-500" style="width: ${Math.min(metrics.coveredPercent, 100)}%"></div>
              </div>
            </div>
          </div>

          <div class="mt-4 pt-2.5 border-t border-border/60 flex items-center justify-between text-[10px] font-mono text-text-secondary">
            <span>Costo Fijo: <b class="text-text-primary">${formatMoney(metrics.fixedCost)}</b></span>
            <a href="#/settings" class="text-primary hover:underline" title="Configurar costos fijos en Ajustes">Configurar</a>
          </div>
        </div>
      </div>

      <!-- Situación Comercial (Pipeline Funnel) -->
      <div class="bg-surface p-5 rounded-md border border-border shadow-card">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <div class="flex items-center gap-2">
            <span class="text-xs font-semibold text-text-secondary uppercase tracking-wider">Situación Comercial (Pipeline)</span>
            <span class="text-[10px] font-mono px-2 py-0.5 rounded bg-surface-subtle border border-border font-medium">
              ${metrics.totalClientsCount} clientes totales
            </span>
          </div>
          <a href="#/pipeline" class="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
            Ver pipeline completo ${getIcon('arrowUpRight', 'w-3 h-3')}
          </a>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <div class="p-3 bg-surface-subtle/70 rounded-md border border-border text-center">
            <span class="text-[11px] font-medium text-text-secondary block">Clientes Potenciales</span>
            <span class="text-2xl font-bold font-mono text-text-primary mt-1 block">${metrics.pipelinePotenciales}</span>
            <span class="text-[10px] text-text-secondary mt-0.5 block">En prospección</span>
          </div>
          <div class="p-3 bg-blue-50/60 rounded-md border border-blue-200 text-center">
            <span class="text-[11px] font-medium text-blue-900 block">Propuesta en Curso</span>
            <span class="text-2xl font-bold font-mono text-blue-700 mt-1 block">${metrics.pipelinePropuestas}</span>
            <span class="text-[10px] text-blue-700/80 mt-0.5 block">Cotización enviada</span>
          </div>
          <div class="p-3 bg-emerald-50/60 rounded-md border border-emerald-200 text-center">
            <span class="text-[11px] font-medium text-emerald-900 block">Ganado / Activo</span>
            <span class="text-2xl font-bold font-mono text-emerald-700 mt-1 block">${metrics.pipelineGanados}</span>
            <span class="text-[10px] text-emerald-700/80 mt-0.5 block">Cuentas cerradas</span>
          </div>
          <div class="p-3 bg-zinc-100/70 rounded-md border border-zinc-200 text-center">
            <span class="text-[11px] font-medium text-zinc-700 block">Descartado / Pausado</span>
            <span class="text-2xl font-bold font-mono text-zinc-600 mt-1 block">${metrics.pipelinePausados}</span>
            <span class="text-[10px] text-zinc-500 mt-0.5 block">Inactivos / archivo</span>
          </div>
        </div>
      </div>

      <!-- Fila de Evoluciones (3 Gráficos Chart.js) -->
      <div class="bg-surface p-5 rounded-md border border-border shadow-card space-y-4">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-3 border-b border-border">
          <div>
            <h2 class="text-sm font-bold text-text-primary uppercase tracking-wide">Evolución Histórica (Últimos 6 Meses)</h2>
            <p class="text-xs text-text-secondary mt-0.5">Tendencia de ingresos, evolución del valor de tu hora y límite de carga de trabajo</p>
          </div>
          <span class="text-[11px] font-mono px-2 py-0.5 rounded bg-surface-subtle border border-border text-text-secondary self-start sm:self-auto">
            Ajuste por inflación IPC
          </span>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-2">
          <!-- Gráfico 1: Ingresos Mensuales -->
          <div class="space-y-2">
            <div class="flex items-center justify-between text-xs">
              <span class="font-bold text-text-primary">1. Ingresos Mensuales</span>
              <span class="font-mono text-[11px] text-emerald-700 font-semibold">Devengado ARS</span>
            </div>
            <div class="h-56 relative w-full bg-surface-subtle/30 rounded p-2 border border-border/50">
              <canvas id="chart-evolucion-ingresos"></canvas>
            </div>
          </div>

          <!-- Gráfico 2: Valor Hora Nominal vs Real -->
          <div class="space-y-2">
            <div class="flex items-center justify-between text-xs">
              <span class="font-bold text-text-primary">2. Valor de tu Hora</span>
              <span class="font-mono text-[11px] text-text-secondary">Nominal vs Real</span>
            </div>
            <div class="h-56 relative w-full bg-surface-subtle/30 rounded p-2 border border-border/50">
              <canvas id="chart-evolucion-valor-hora"></canvas>
            </div>
          </div>

          <!-- Gráfico 3: Horas Trabajadas -->
          <div class="space-y-2">
            <div class="flex items-center justify-between text-xs">
              <span class="font-bold text-text-primary">3. Horas Trabajadas</span>
              <span class="font-mono text-[11px] text-text-secondary">Meta: 160h</span>
            </div>
            <div class="h-56 relative w-full bg-surface-subtle/30 rounded p-2 border border-border/50">
              <canvas id="chart-evolucion-horas"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- Asymmetric 2-Column Section (Tarjetas Inferiores Intactas) -->
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <!-- Left (7 cols): Proyectos Activos -->
        <div class="lg:col-span-7 bg-surface rounded-md border border-border shadow-card p-6 flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between pb-4 border-b border-border">
              <div class="flex items-center gap-2">
                <h2 class="text-base font-bold text-text-primary">Proyectos Activos</h2>
                <span class="text-xs font-mono px-2 py-0.5 rounded bg-surface-subtle border border-border font-medium">
                  ${metrics.activeProjectsCount} en curso
                </span>
              </div>
              <a href="#/projects" class="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
                Ver todos ${getIcon('arrowUpRight', 'w-3 h-3')}
              </a>
            </div>

            <div class="divide-y divide-border/60 mt-2">
              ${metrics.activeProjects.length === 0 ? `
                <div class="py-12 text-center text-text-secondary text-sm">No hay proyectos en curso registrados actualmente.</div>
              ` : metrics.activeProjects.map(p => {
                const isTm = p.billing_mode === 'time_and_materials';
                const isRecurring = p.revenue_type === 'recurrente';
                return `
                  <div class="py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
                    <div class="space-y-1">
                      <div class="flex items-center gap-2">
                        <a href="#/projects" class="font-bold text-sm text-text-primary hover:text-primary-hover cursor-pointer">
                          ${p.name}
                        </a>
                        <span class="text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface-subtle border border-border text-text-secondary">
                          ${p.client?.name || 'Cliente general'}
                        </span>
                      </div>
                      <div class="flex items-center gap-2 text-xs text-text-secondary">
                        <span class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                          isTm 
                            ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                            : 'bg-zinc-100 text-zinc-700 border border-zinc-200'
                        }">
                          ${isTm ? 'Por Horas Reales' : 'Presupuesto Fijo'}
                        </span>
                        <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold ${
                          isRecurring 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                            : 'bg-surface-subtle text-text-secondary border border-border'
                        }">
                          ${isRecurring ? 'Recurrente' : 'Puntual'}
                        </span>
                        <span>•</span>
                        <span>${p.client?.channel || 'Directo'}</span>
                      </div>
                    </div>
                    <div class="text-right">
                      <p class="text-sm font-mono font-bold text-text-primary">
                        ${formatMoney(isTm ? p.accrued_amount : p.budget_amount)}
                      </p>
                      <p class="text-[11px] font-mono text-text-secondary">
                        ${isTm 
                          ? `${formatHours(p.total_actual_hours)} imputadas` 
                          : `Presup: ${p.budget_hours || 0}h (${formatHours(p.total_actual_hours)} cons.)`}
                      </p>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>

          <div class="pt-4 mt-2 border-t border-border flex justify-end">
            <a href="#/projects" class="px-3.5 py-2 text-xs font-semibold rounded-md bg-surface-subtle border border-border hover:bg-border/40 transition-colors text-text-primary">
              Ir al Administrador de Proyectos
            </a>
          </div>
        </div>

        <!-- Right (5 cols): Tareas Urgentes y Knowledge OS -->
        <div class="lg:col-span-5 space-y-6">
          <!-- Tareas Urgentes -->
          <div class="bg-surface rounded-md border border-border shadow-card p-6">
            <div class="flex items-center justify-between pb-3.5 border-b border-border">
              <h2 class="text-base font-bold text-text-primary">Próximos Vencimientos</h2>
              <a href="#/projects" class="text-xs text-primary font-semibold hover:underline">Ver WBS</a>
            </div>

            <div class="divide-y divide-border/60 mt-2">
              ${metrics.urgentTasks.length === 0 ? `
                <div class="py-8 text-center text-text-secondary text-xs">Sin tareas urgentes pendientes.</div>
              ` : metrics.urgentTasks.map(t => {
                const isUrgent = t.priority === 'Alta' || t.priority === 'Urgente';
                return `
                  <div class="py-2.5 flex items-start justify-between gap-3">
                    <div class="space-y-0.5">
                      <p class="text-xs font-semibold text-text-primary leading-tight">${t.title}</p>
                      <p class="text-[11px] text-text-secondary truncate">${t.project?.name || 'Proyecto'}</p>
                    </div>
                    <span class="shrink-0 text-[10px] font-mono font-semibold px-2 py-0.5 rounded border ${
                      isUrgent 
                        ? 'bg-red-50 text-red-700 border-red-200' 
                        : 'bg-surface-subtle text-text-secondary border-border'
                    }">
                      ${t.priority}
                    </span>
                  </div>
                `;
              }).join('')}
            </div>
          </div>

          <!-- Knowledge OS Card -->
          <div class="bg-primary text-white rounded-md p-6 shadow-card">
            <div class="flex items-center justify-between mb-3">
              <span class="text-xs font-mono font-medium tracking-wider text-zinc-400 uppercase">Knowledge OS</span>
              ${getIcon('book', 'w-4 h-4 text-zinc-300')}
            </div>
            <h3 class="text-lg font-bold">Base de Conocimiento OKF</h3>
            <p class="text-xs text-zinc-300 mt-1 leading-relaxed">
              Metodologías de diagnóstico, procedimientos estándar y convención de tarifas versionadas en Git y auditadas con OpenKnowledge.
            </p>
            <div class="mt-4 pt-3 border-t border-zinc-800 flex items-center justify-between">
              <a href="#/knowledge" class="inline-flex items-center gap-1.5 text-xs font-semibold text-white hover:text-zinc-200 transition-colors">
                Explorar Documentos ${getIcon('arrowUpRight', 'w-3.5 h-3.5')}
              </a>
              <span class="text-[10px] font-mono text-zinc-400">OKF v0.2</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal de Desglose de Horas por Proyecto -->
      <div id="modal-hours-breakdown" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm hidden">
        <div class="bg-surface border border-border rounded-lg shadow-xl w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden animate-fade-in">
          <div class="p-4 border-b border-border flex items-center justify-between bg-surface-subtle/50">
            <div>
              <h3 class="font-bold text-sm text-text-primary">Desglose de Horas en ${monthNames[selectedMonth]}</h3>
              <p class="text-[11px] text-text-secondary font-mono">Total imputado: ${formatHours(metrics.currentMonthHours)}</p>
            </div>
            <button id="btn-close-hours-modal" class="p-1 text-text-secondary hover:text-text-primary rounded">
              ${getIcon('x', 'w-4 h-4')}
            </button>
          </div>

          <div class="p-4 overflow-y-auto divide-y divide-border/60">
            ${metrics.hoursByProject.length === 0 ? `
              <p class="text-center text-xs text-text-secondary py-6">No se registraron horas imputadas en este período.</p>
            ` : metrics.hoursByProject.map(p => `
              <div class="py-2.5 flex items-center justify-between gap-3">
                <div class="space-y-0.5">
                  <div class="flex items-center gap-2">
                    <span class="text-xs font-bold text-text-primary">${p.name}</span>
                    <span class="text-[9px] font-mono px-1.5 py-0.5 rounded border ${
                      p.isInternal 
                        ? 'bg-amber-50 text-amber-800 border-amber-200' 
                        : p.revenueType === 'recurrente' 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                          : 'bg-blue-50 text-blue-700 border-blue-200'
                    }">
                      ${p.isInternal ? 'Interna' : p.revenueType === 'recurrente' ? 'Recurrente' : 'Puntual'}
                    </span>
                  </div>
                  <span class="text-[10px] text-text-secondary font-mono">${p.billingMode === 'time_and_materials' ? 'Por Horas Reales' : 'Presupuesto Fijo'}</span>
                </div>
                <div class="text-right">
                  <span class="text-xs font-mono font-bold text-text-primary block">${formatHours(p.hours)}</span>
                  <span class="text-[10px] font-mono text-text-secondary block">${p.percent}% del mes</span>
                </div>
              </div>
            `).join('')}
          </div>

          <div class="p-3 border-t border-border bg-surface-subtle/50 flex justify-end">
            <button id="btn-dismiss-hours-modal" class="px-3.5 py-1.5 text-xs font-semibold rounded bg-surface border border-border hover:bg-surface-subtle text-text-primary">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
}

// Inicialización de eventos e interactividad del Dashboard
export function initDashboardEvents() {
  destroyCharts();

  // 1. Selector de Mes y Año
  const selectMonth = document.getElementById('dashboard-filter-month');
  const selectYear = document.getElementById('dashboard-filter-year');
  const btnReset = document.getElementById('btn-reset-current-month');

  if (selectMonth) {
    selectMonth.onchange = () => {
      selectedMonth = parseInt(selectMonth.value, 10);
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  if (selectYear) {
    selectYear.onchange = () => {
      selectedYear = parseInt(selectYear.value, 10);
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  if (btnReset) {
    btnReset.onclick = () => {
      selectedMonth = now.getMonth();
      selectedYear = now.getFullYear();
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  // 2. Modal de Desglose de Horas
  const modalHours = document.getElementById('modal-hours-breakdown');
  const btnOpenHours = document.getElementById('btn-open-hours-breakdown');
  const btnCloseHours = document.getElementById('btn-close-hours-modal');
  const btnDismissHours = document.getElementById('btn-dismiss-hours-modal');

  if (btnOpenHours && modalHours) {
    btnOpenHours.onclick = () => modalHours.classList.remove('hidden');
  }
  [btnCloseHours, btnDismissHours].forEach(btn => {
    if (btn && modalHours) {
      btn.onclick = () => modalHours.classList.add('hidden');
    }
  });

  // 3. Renderizar gráficos Chart.js
  const currentRequestId = ++dashboardRenderRequestId;
  DataService.getDashboardMetrics({ month: selectedMonth, year: selectedYear }).then(metrics => {
    // Si se disparó otra petición más reciente o el usuario cambió de vista, abortar
    if (currentRequestId !== dashboardRenderRequestId) return;

    const history = metrics.evolutionHistory || [];
    const labels = history.map(h => h.label);

    // Gráfico 1: Ingresos Mensuales
    const ctxIngresos = document.getElementById('chart-evolucion-ingresos');
    if (ctxIngresos) {
      const existingIngresos = Chart.getChart(ctxIngresos);
      if (existingIngresos) {
        try { existingIngresos.destroy(); } catch (e) {}
      }

      chartIngresos = new Chart(ctxIngresos, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Ingresos Devengados ($)',
            data: history.map(h => h.ingresos),
            backgroundColor: 'rgba(5, 150, 105, 0.75)',
            borderColor: '#059669',
            borderWidth: 1.5,
            borderRadius: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => ` $${Number(ctx.raw || 0).toLocaleString('es-AR')}`
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 10 } }
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0, 0, 0, 0.05)' },
              ticks: {
                font: { size: 9 },
                callback: (v) => `$${(v / 1000).toFixed(0)}k`
              }
            }
          }
        }
      });
    }

    // Gráfico 2: Valor Hora Nominal vs Real
    const ctxValorHora = document.getElementById('chart-evolucion-valor-hora');
    if (ctxValorHora) {
      const existingValorHora = Chart.getChart(ctxValorHora);
      if (existingValorHora) {
        try { existingValorHora.destroy(); } catch (e) {}
      }

      chartValorHora = new Chart(ctxValorHora, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Valor Hora Real (ajust. inflac.)',
              data: history.map(h => h.valorHoraReal),
              borderColor: '#059669',
              backgroundColor: 'rgba(5, 150, 105, 0.1)',
              fill: true,
              tension: 0.3,
              pointRadius: 4,
              borderWidth: 2
            },
            {
              label: 'Valor Hora Nominal',
              data: history.map(h => h.valorHoraNominal),
              borderColor: '#71717a',
              borderDash: [4, 4],
              tension: 0.3,
              pointRadius: 3,
              borderWidth: 1.5
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: { boxWidth: 12, font: { size: 10 } }
            },
            tooltip: {
              callbacks: {
                label: (ctx) => ` ${ctx.dataset.label}: $${Number(ctx.raw || 0).toLocaleString('es-AR')}/h`
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 10 } }
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0, 0, 0, 0.05)' },
              ticks: {
                font: { size: 9 },
                callback: (v) => `$${Number(v).toLocaleString('es-AR')}`
              }
            }
          }
        }
      });
    }

    // Gráfico 3: Horas Trabajadas
    const ctxHoras = document.getElementById('chart-evolucion-horas');
    if (ctxHoras) {
      const existingHoras = Chart.getChart(ctxHoras);
      if (existingHoras) {
        try { existingHoras.destroy(); } catch (e) {}
      }

      chartHoras = new Chart(ctxHoras, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Horas Reales',
            data: history.map(h => h.horas),
            backgroundColor: 'rgba(24, 24, 27, 0.8)',
            borderRadius: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => ` ${ctx.raw} horas trabajadas`
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 10 } }
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0, 0, 0, 0.05)' },
              ticks: { font: { size: 9 } }
            }
          }
        }
      });
    }
  });
}
